// src/Pages/Commerce/ProductManagement.js
//
// Blitz Commerce — Alpha
// Product link creation + sales metrics + purchasers (CRM by proxy) + returns.
//
// Gated to AlphaFree company accounts. Navbar.js already hides the nav
// entry for non-AlphaFree companies; this page re-checks the same
// condition so the route itself isn't reachable by pasting a URL.
//
// Backend endpoints referenced below (client.commerce.*) do not exist
// yet — this page is being built ahead of the API per Alex's plan.
// See the bottom of this file for the exact shape each call expects
// back, so the backend can be built to match.
//
// checkout_url is a stable link to the /checkout/:slug landing page
// (ProductCheckoutPage.js — path matches the checkout_url format
// documented in backend-t2a.md), NOT a raw T2A pay-request link — a T2A
// request is single-use (dies after one payment), so it can't be the
// product's permanent link. ProductCheckoutPage mints a fresh T2A
// pay-request per buyer at checkout time via
// client.commerce.requestProductPayment, then immediately pays against
// that token in the same step (balanceLedger.payRequestByToken) and
// redirects straight to Stripe/PayPal's approval_url — no intermediate
// /pay-request/:token screen.
//
// Where the money lands: per backend-t2a.md there are two separate
// money-movement systems (T2A → GlobalUser.balance, vs. a Commerce-
// specific ledger → CompanyModel.balance) — but CompanyModel.balance
// is NOT used in this version of Blitz. Sale proceeds are meant to
// route through T2A into the specific GlobalUser (company user) who
// created the product, same as every other T2A flow in this app. That's
// why `ownerGlobalUserId` (via client.balanceLedger.fetchMyBalance()) is
// captured below and sent as owner_global_user_id on product creation —
// the backend needs it to know who the T2A "requester" is at checkout
// time, since the buyer has no session telling it who they're paying.
//
// For the money-movement side of a checkout (what a buyer paid, and
// what actually landed) see CommerceTransactions.js — that's an
// independent page (routes.commerceTransactions), linked from the
// header below, rather than a tab here. This page is about managing
// product links and who bought them; that one is about how the money
// moved.

import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Tooltip,
  Skeleton,
  Alert,
  Grid,
  Avatar,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import client from "../../API";
import { BASE_API } from "../../API/http-client.js";
import useAuth from "../../Hooks/use-auth";
import { useNavigate } from "react-router-dom";
import routes from "../../Config/routes.js";
import CreateProductLinkDialog from "./CreateProductLinkDialog.js";
import ImportSalesHistoryDialog from "./ImportSalesHistoryDialog.js";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { COMMERCE_BRAND, formatCurrency, StatCard, EmptyState } from "./commerceShared.js";

/*------------------------------------------------------------------
  Tab: Products & Links
-------------------------------------------------------------------*/
function ProductsTab({ products, isLoading, onCreateClick }) {
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyLink = (product) => {
    navigator.clipboard.writeText(product.checkout_url);
    setCopiedId(product.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Products
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon />}
          onClick={onCreateClick}
          sx={{
            textTransform: "none",
            bgcolor: COMMERCE_BRAND.sky,
            "&:hover": { bgcolor: COMMERCE_BRAND.skyDark },
          }}
        >
          Create product link
        </Button>
      </Box>

      {isLoading ? (
        <Skeleton variant="rounded" height={220} />
      ) : products?.length ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Checkout link</TableCell>
                <TableCell align="right">Units sold</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        variant="rounded"
                        src={p.image_url || undefined}
                        sx={{
                          width: 34,
                          height: 34,
                          bgcolor: alpha(COMMERCE_BRAND.sky, 0.12),
                          color: COMMERCE_BRAND.skyDark,
                        }}
                      >
                        <StorefrontOutlinedIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created {new Date(p.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{formatCurrency(p.price)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.active ? "Active" : "Paused"}
                      color={p.active ? "success" : "default"}
                      variant={p.active ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.checkout_url}
                      </Typography>
                      <Tooltip title={copiedId === p.id ? "Copied!" : "Copy link"}>
                        <IconButton size="small" onClick={() => handleCopyLink(p)}>
                          <ContentCopyOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open checkout page">
                        <IconButton
                          size="small"
                          component="a"
                          href={p.checkout_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{p.units_sold ?? 0}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(p.revenue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <EmptyState
            icon={<StorefrontOutlinedIcon fontSize="inherit" />}
            title="No product links yet"
            description="Create your first product link to start accepting payments through your WooCommerce or WordPress store."
            action={
              <Button
                variant="outlined"
                startIcon={<AddOutlinedIcon />}
                onClick={onCreateClick}
                sx={{ textTransform: "none", mt: 1 }}
              >
                Create product link
              </Button>
            }
          />
        </Paper>
      )}
    </Box>
  );
}

/*------------------------------------------------------------------
  Tab: Sales & Metrics (revenue-by-product breakdown — headline stats
  now live in the persistent header row instead of duplicating here)
-------------------------------------------------------------------*/
function MetricsTab({ metrics, isLoading }) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Revenue by product
      </Typography>
      {isLoading ? (
        <Skeleton variant="rounded" height={160} />
      ) : metrics?.by_product?.length ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Orders</TableCell>
                <TableCell align="right">Revenue</TableCell>
                <TableCell align="right">Returns</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.by_product.map((row) => (
                <TableRow key={row.product_id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align="right">{row.orders}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.revenue)}
                  </TableCell>
                  <TableCell align="right">{row.returns}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <EmptyState
            icon={<TrendingUpOutlinedIcon fontSize="inherit" />}
            title="No sales yet"
            description="Once your product links start converting, revenue and return metrics will show up here."
          />
        </Paper>
      )}
    </Box>
  );
}

/*------------------------------------------------------------------
  Tab: Purchasers (CRM by proxy)
-------------------------------------------------------------------*/
function PurchasersTab({ purchasers, isLoading }) {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Purchasers
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Every completed order automatically creates or updates a client
          record in your CRM.
        </Typography>
      </Box>

      {isLoading ? (
        <Skeleton variant="rounded" height={220} />
      ) : purchasers?.length ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Purchaser</TableCell>
                <TableCell>Product</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">CRM record</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchasers.map((row) => (
                <TableRow key={row.order_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.customer_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.customer_email}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.product_name}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.amount)}
                  </TableCell>
                  <TableCell>
                    {new Date(row.purchased_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === "fulfilled"
                          ? "success"
                          : row.status === "refunded"
                          ? "error"
                          : "default"
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="text"
                      sx={{ textTransform: "none" }}
                      onClick={() =>
                        navigate(routes.clientProfile.replace(":clientId", row.client_id))
                      }
                    >
                      View in CRM
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <EmptyState
            icon={<PeopleAltOutlinedIcon fontSize="inherit" />}
            title="No purchasers yet"
            description="When a customer completes checkout, they'll show up here and a matching client record will be created in your CRM automatically."
          />
        </Paper>
      )}
    </Box>
  );
}

/*------------------------------------------------------------------
  Tab: Returns & Issues
-------------------------------------------------------------------*/
function ReturnsTab({ returns, isLoading }) {
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Returns & issues
      </Typography>

      {isLoading ? (
        <Skeleton variant="rounded" height={220} />
      ) : returns?.length ? (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Purchaser</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Filed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returns.map((row) => (
                <TableRow key={row.return_id} hover>
                  <TableCell>{row.order_id}</TableCell>
                  <TableCell>{row.customer_name}</TableCell>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(row.amount)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === "approved"
                          ? "error"
                          : row.status === "denied"
                          ? "success"
                          : "warning"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(row.filed_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <EmptyState
            icon={<AssignmentReturnOutlinedIcon fontSize="inherit" />}
            title="No returns filed"
            description="Chargebacks and refund requests against your product links will appear here as they come in."
          />
        </Paper>
      )}
    </Box>
  );
}

/*------------------------------------------------------------------
  Tab: Developer portal (API key + webhooks)

  Restores the tab added in 09046bcd2 ("developer portal tab + admin
  commerce onboarding") that was accidentally dropped when #469 merged
  from an older base. Doc content below is rewritten for the embedded-
  checkout contract (blitz-backend/docs/commerce/README.md): `total`
  replaces `subtotal` as the request field (now the full customer-facing
  amount, not the merchant's pre-fee take), the fee is DEDUCTED from the
  merchant's proceeds instead of added on top, and checkout_url now points
  at a Blitz-hosted embedded-checkout page instead of straight to Stripe.
-------------------------------------------------------------------*/

// One-time secret reveal (raw API key on reroll, or webhook signing secret) —
// shown inline right after the action that produced it, with a copy button and
// a "store it now" warning, since the backend never returns it again.
function OneTimeSecret({ label, value, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Alert severity="warning" icon={false} sx={{ mt: 1.5, borderRadius: 2 }} onClose={onDismiss}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
        {label} — copy it now, it won't be shown again
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
          {value}
        </Typography>
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          <IconButton size="small" onClick={copy} color={copied ? "success" : "default"}>
            <ContentCopyOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Alert>
  );
}

// Monospace code block that scrolls horizontally instead of overflowing the page.
const DOC_PRE_SX = {
  fontFamily: "monospace",
  fontSize: 12,
  bgcolor: "action.hover",
  borderRadius: 1,
  p: 1.5,
  m: 0,
  mt: 1,
  overflowX: "auto",
  whiteSpace: "pre",
};

// `total` is the full amount the customer pays (inclusive of tax/shipping/
// items) — NOT what you want to receive. The 3% fee is deducted from it;
// you never gross it up yourself.
const DOC_CHECKOUT_REQUEST = `POST https://<commerce-api-host>/thirdparty/commerce/checkout
Authorization: Bearer <your_api_key>
Content-Type: application/json

{
  "external_order_ref": "cart_8f21",
  "items": [{ "sku": "TSHIRT", "qty": 2, "price": "50.00" }],
  "total": "100.00",
  "currency": "USD",
  "success_url": "https://shop.example.com/thanks",
  "cancel_url":  "https://shop.example.com/cart"
}`;

const DOC_CHECKOUT_RESPONSE = `{
  "session_id": "TXN-20260101120000-A1B2C",
  "checkout_url": "https://www.useblitz.co/commerce/pay/TXN-.../<checkout_token>",
  "subtotal": "97.00",
  "company_fee": "3.00",
  "total": "100.00",
  "currency": "USD",
  "status": "awaiting_payment",
  "reused": false
}`;

const DOC_WEBHOOK_PAYLOAD = `POST <your webhook url>
Content-Type: application/json
X-Blitz-Event: checkout.paid
X-Blitz-Timestamp: 1710000000
X-Blitz-Signature: <hex hmac>

{
  "event": "checkout.paid",
  "created_at": "2026-01-01T00:00:00Z",
  "data": {
    "session_id": "TXN-20260101120000-A1B2C",
    "external_order_ref": "cart_8f21",
    "subtotal": "97.00",
    "company_fee": "3.00",
    "total": "100.00",
    "currency": "USD",
    "items": [{ "sku": "TSHIRT", "qty": 2, "price": "50.00" }]
  }
}`;

const DOC_VERIFY_JS = `const crypto = require("crypto");

// rawBody MUST be the exact bytes received — do not JSON.parse then re-stringify.
function verify(rawBody, timestamp, signature, secret) {
  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${timestamp}.\` + rawBody)
    .digest("hex");
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(signature),
  );
}`;

function DocAccordion({ title, children }) {
  return (
    <Accordion disableGutters sx={{ "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

// In-app developer guide for the Commerce API, the hosted checkout redirect,
// and webhooks. Content mirrors the backend contract
// (blitz-backend/docs/commerce/README.md + commerce_checkout_service.py +
// commerce_webhook_service.py).
function DeveloperDocs() {
  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <MenuBookOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Developer guide
        </Typography>
      </Stack>

      {/* Prebuilt integration: served on demand from the backend (always
          matches whatever's currently deployed — never a stale committed
          copy), so this is a plain download link, not an API call. */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              WooCommerce plugin
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Redirects checkout to Blitz Commerce and syncs order status automatically via webhook.
            </Typography>
          </Box>
          <Button
            component="a"
            href={`${BASE_API}/commerce/plugins/woocommerce`}
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Download plugin
          </Button>
        </Stack>
      </Paper>

      <DocAccordion title="Getting started">
        <Typography variant="body2" component="div">
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            <li>Copy your <strong>API key</strong> above and store it as a secret in your backend — never ship it to a browser.</li>
            <li>Register your <strong>webhook URL</strong> (must be public HTTPS) and copy the <strong>signing secret</strong>.</li>
            <li>Flip the <strong>Enabled</strong> switch once your receiver is live — endpoints start disabled.</li>
            <li>From your backend, create a checkout session with the API key, then redirect the customer's browser to the <code>checkout_url</code> you get back.</li>
            <li>We notify your webhook when the customer finishes paying.</li>
          </ol>
        </Typography>
      </DocAccordion>

      <DocAccordion title="1. Create a checkout session (server-to-server)">
        <Typography variant="body2">
          Called from <strong>your backend only</strong> — the API key must never
          reach the customer's browser. <code>total</code> is the full amount the
          customer will pay (tax/shipping/items already included); the fee below
          is deducted from what you're credited, not added on top. Fees and
          totals are always recomputed server-side, and re-POSTing the same{" "}
          <code>external_order_ref</code> returns the existing session
          (idempotent) instead of creating a duplicate.
        </Typography>
        <Box component="pre" sx={DOC_PRE_SX}>{DOC_CHECKOUT_REQUEST}</Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          Response:
        </Typography>
        <Box component="pre" sx={DOC_PRE_SX}>{DOC_CHECKOUT_RESPONSE}</Box>
      </DocAccordion>

      <DocAccordion title="2. Redirect the customer to checkout_url">
        <Typography variant="body2" component="div">
          <code>checkout_url</code> is a page <strong>on Blitz's own domain</strong>{" "}
          (<code>/commerce/pay/&lt;session_id&gt;/&lt;checkout_token&gt;</code>) —
          not a redirect to an external payment page. Send the customer's
          browser there (a redirect from your server, or a link/button on
          your existing checkout page); the payment form is embedded right
          on that page, so the customer never leaves a Blitz- or
          your-branded flow.
          <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
            <li>
              The value in the URL — the <strong>checkout token</strong> — is a{" "}
              <strong>third, separate credential</strong> from the two above.
              It is <strong>not</strong> your API key and <strong>not</strong>{" "}
              your webhook signing secret. It's generated automatically,
              scoped to this one checkout session only, and safe to appear in
              a URL — it proves the session came from your integration
              without ever putting your API key in the customer's browser.
            </li>
            <li>After payment, we send the customer's browser to a Blitz-hosted confirmation screen, which redirects them to the <code>success_url</code> you provided (falls back to a generic "you can close this window" message if you didn't set one).</li>
            <li>This confirmation redirect is a UX convenience only — treat the <strong>webhook</strong> below as the source of truth for whether the order was actually paid, since a customer can close the tab before the redirect fires.</li>
          </ul>
        </Typography>
      </DocAccordion>

      <DocAccordion title="3. Webhook events & payload">
        <Typography variant="body2">
          When a checkout is paid we POST a <code>checkout.paid</code> event to your URL
          with these headers. Respond <code>2xx</code> to acknowledge.
        </Typography>
        <Box component="pre" sx={DOC_PRE_SX}>{DOC_WEBHOOK_PAYLOAD}</Box>
      </DocAccordion>

      <DocAccordion title="Verifying the signature">
        <Typography variant="body2" component="div">
          Works in any language — it's plain HMAC-SHA256, in every standard library
          (Node <code>crypto</code>, PHP <code>hash_hmac</code>, Ruby{" "}
          <code>OpenSSL::HMAC</code>, Go <code>crypto/hmac</code>, Java{" "}
          <code>Mac</code>, .NET <code>HMACSHA256</code>). The algorithm:
          <ol style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
            <li>Read the <code>X-Blitz-Timestamp</code> header and the <strong>raw request body</strong> (the exact bytes — do <strong>not</strong> parse and re-serialize the JSON, that changes the bytes and breaks the check).</li>
            <li>Compute <code>HMAC-SHA256(secret, "{"{timestamp}."}" + rawBody)</code> as a lowercase hex string.</li>
            <li>Compare it constant-time against <code>X-Blitz-Signature</code>, and reject stale timestamps to prevent replays.</li>
          </ol>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Example (Node.js — the same three steps in any language):
        </Typography>
        <Box component="pre" sx={DOC_PRE_SX}>{DOC_VERIFY_JS}</Box>
      </DocAccordion>

      <DocAccordion title="Retries & delivery">
        <Typography variant="body2" component="div">
          A non-<code>2xx</code> response or network error is retried with exponential
          backoff — <strong>5m, 15m, 1h, 1d, 3d, 7d</strong> (6 attempts) — then marked
          failed. Note:
          <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
            <li>Events fired while the webhook is <strong>disabled</strong> are skipped and are <strong>not</strong> resent when you re-enable.</li>
            <li>Return quickly (do heavy work async) so deliveries aren't marked failed on timeout.</li>
          </ul>
        </Typography>
      </DocAccordion>

      <DocAccordion title="Troubleshooting">
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li><strong>No events arriving</strong> — the webhook is disabled, or the URL isn't public HTTPS (localhost, private, and <code>*.local</code>/<code>*.internal</code> hosts are rejected).</li>
            <li><strong>Signature mismatch</strong> — you used a re-serialized body instead of the raw bytes, the wrong secret, or you just updated the URL (saving a URL issues a new signing secret — copy it again above).</li>
            <li><strong>401 / invalid API key</strong> — the key was rerolled; rerolling revokes the old key, so copy the current one above.</li>
            <li><strong>checkout_url 404s for the customer</strong> — the session may already be paid/failed (checkout_url is only valid while a session is <code>awaiting_payment</code>), or the URL was truncated/modified — redirect using the exact string returned, don't reconstruct it.</li>
          </ul>
        </Typography>
      </DocAccordion>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        Need more? Ask your account manager for the full Commerce API reference.{" "}
        <Link href="mailto:support@useblitz.co" underline="hover">
          support@useblitz.co
        </Link>
      </Typography>
    </Box>
  );
}

function DeveloperPortalTab({ integrationId }) {
  const queryClient = useQueryClient();

  // ── API key ──────────────────────────────────────────────
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmReroll, setConfirmReroll] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const {
    data: keyData,
    isLoading: isLoadingKey,
    isError: isKeyError,
    error: keyError,
  } = useQuery(
    ["commerce", "apiKey", integrationId],
    () => client.commerce.revealApiKey(integrationId),
    { enabled: !!integrationId, staleTime: 1000 * 60 },
  );
  const apiKey = keyData?.api_key || null;

  const rerollMutation = useMutation(
    () => client.commerce.rerollApiKey(integrationId),
    {
      onSuccess: (res) => {
        setConfirmReroll(false);
        setNewKey(res?.api_key || null);
        queryClient.invalidateQueries(["commerce", "apiKey", integrationId]);
      },
    },
  );

  const copyKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const maskedKey = apiKey
    ? `${apiKey.slice(0, 15)}${"•".repeat(Math.max(apiKey.length - 15, 8))}`
    : "";

  // ── Webhook ──────────────────────────────────────────────
  const {
    data: webhookData,
    isLoading: isLoadingWebhook,
    isError: isWebhookError,
    error: webhookError,
  } = useQuery(
    ["commerce", "webhook", integrationId],
    () => client.commerce.getWebhook(integrationId),
    { enabled: !!integrationId, staleTime: 1000 * 60 },
  );
  const webhook = webhookData?.webhook || null;

  const [urlInput, setUrlInput] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    setUrlInput(webhook?.url || "");
  }, [webhook?.url]);

  // Signing secret is re-fetchable in full (owner-only) so it stays retrievable
  // and copy-pasteable — not just shown once at save time.
  const { data: secretData } = useQuery(
    ["commerce", "webhookSecret", integrationId],
    () => client.commerce.revealWebhookSecret(integrationId),
    { enabled: !!integrationId && !!webhook, staleTime: 1000 * 60 },
  );
  const signingSecret = secretData?.signing_secret || null;
  const maskedSecret = signingSecret
    ? `${signingSecret.slice(0, 6)}${"•".repeat(Math.max(signingSecret.length - 6, 8))}`
    : "";

  const copySecret = () => {
    if (!signingSecret) return;
    navigator.clipboard.writeText(signingSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 1500);
  };

  const urlIsHttps = /^https:\/\//i.test(urlInput.trim());
  const urlError = urlTouched && !!urlInput.trim() && !urlIsHttps;

  const saveWebhookMutation = useMutation(
    () =>
      client.commerce.setWebhook({
        integration_id: integrationId,
        url: urlInput.trim(),
      }),
    {
      onSuccess: () => {
        // Saving a URL rotates the signing secret server-side; refresh both.
        queryClient.invalidateQueries(["commerce", "webhook", integrationId]);
        queryClient.invalidateQueries(["commerce", "webhookSecret", integrationId]);
      },
    },
  );

  const toggleWebhookMutation = useMutation(
    (enable) =>
      enable
        ? client.commerce.enableWebhook(integrationId)
        : client.commerce.disableWebhook(integrationId),
    {
      onSuccess: () => queryClient.invalidateQueries(["commerce", "webhook", integrationId]),
    },
  );

  const rotateSecretMutation = useMutation(
    () => client.commerce.rotateWebhookSecret(integrationId),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(["commerce", "webhookSecret", integrationId]),
    },
  );

  if (!integrationId) {
    return (
      <EmptyState
        icon={<VpnKeyOutlinedIcon fontSize="inherit" />}
        title="Commerce isn't set up for your account yet"
        description="Your API key and webhooks appear here once commerce has been enabled for your account. Reach out to your account manager to get started."
      />
    );
  }

  return (
    <Box>
      {/* ── API key ─────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <VpnKeyOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          API key
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Authenticate requests to the Blitz Commerce API with this key. Keep it
        secret — anyone with it can act on your behalf.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        {isLoadingKey ? (
          <Skeleton variant="rounded" height={48} />
        ) : isKeyError ? (
          // A failed request must NOT look like "no key exists" — that ambiguity
          // hides the real cause (auth, 404, network) behind an empty state.
          <Alert severity="error">
            Couldn't load your API key
            {keyError?.response?.status ? ` (HTTP ${keyError.response.status})` : ""}
            :{" "}
            {keyError?.response?.data?.error ||
              keyError?.message ||
              "unknown error"}
            . Refresh to retry — if this persists, contact support.
          </Alert>
        ) : apiKey ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TextField
              value={revealed ? apiKey : maskedKey}
              fullWidth
              size="small"
              InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
            />
            <Tooltip title={revealed ? "Hide" : "Reveal"}>
              <IconButton size="small" onClick={() => setRevealed((v) => !v)}>
                {revealed ? (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={copyKey} color={copied ? "success" : "default"}>
                <ContentCopyOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reroll key">
              <IconButton
                size="small"
                onClick={() => setConfirmReroll(true)}
                disabled={rerollMutation.isLoading}
              >
                <AutorenewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              No active API key yet.
            </Typography>
            <Button
              size="small"
              startIcon={<AutorenewOutlinedIcon />}
              onClick={() => setConfirmReroll(true)}
              sx={{ textTransform: "none" }}
            >
              Generate key
            </Button>
          </Stack>
        )}

        {newKey && (
          <OneTimeSecret label="New API key" value={newKey} onDismiss={() => setNewKey(null)} />
        )}
        {rerollMutation.isError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {rerollMutation.error?.response?.data?.error ||
              "Couldn't reroll the key. Try again."}
          </Alert>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* ── Webhook ─────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <WebhookOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Webhook endpoint
        </Typography>
        {webhook && (
          <Chip
            size="small"
            label={webhook.enabled ? "Enabled" : "Disabled"}
            color={webhook.enabled ? "success" : "default"}
            variant={webhook.enabled ? "filled" : "outlined"}
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        We POST commerce events to this HTTPS URL. Verify each request with the
        signing secret shown when you save.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        {isLoadingWebhook ? (
          <Skeleton variant="rounded" height={48} />
        ) : isWebhookError ? (
          <Alert severity="error">
            Couldn't load your webhook
            {webhookError?.response?.status
              ? ` (HTTP ${webhookError.response.status})`
              : ""}
            :{" "}
            {webhookError?.response?.data?.error ||
              webhookError?.message ||
              "unknown error"}
            . Refresh to retry.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                label="Endpoint URL"
                placeholder="https://api.yourdomain.com/blitz/webhook"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onBlur={() => setUrlTouched(true)}
                error={urlError}
                helperText={
                  urlError
                    ? "Must be an HTTPS URL"
                    : "Saving a URL issues a new signing secret."
                }
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WebhookOutlinedIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={() => saveWebhookMutation.mutate()}
                disabled={
                  saveWebhookMutation.isLoading ||
                  !urlInput.trim() ||
                  !urlIsHttps ||
                  urlInput.trim() === (webhook?.url || "")
                }
                sx={{
                  mt: 0.25,
                  textTransform: "none",
                  bgcolor: COMMERCE_BRAND.sky,
                  "&:hover": { bgcolor: COMMERCE_BRAND.skyDark },
                  whiteSpace: "nowrap",
                }}
              >
                {saveWebhookMutation.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : webhook ? (
                  "Update"
                ) : (
                  "Register"
                )}
              </Button>
            </Box>

            {webhook && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!webhook.enabled}
                      onChange={(e) => toggleWebhookMutation.mutate(e.target.checked)}
                      disabled={toggleWebhookMutation.isLoading}
                    />
                  }
                  label={webhook.enabled ? "Enabled" : "Disabled"}
                />
                <Button
                  size="small"
                  startIcon={<AutorenewOutlinedIcon />}
                  onClick={() => rotateSecretMutation.mutate()}
                  disabled={rotateSecretMutation.isLoading}
                  sx={{ textTransform: "none" }}
                >
                  Rotate signing secret
                </Button>
              </Stack>
            )}

            {webhook && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  Signing secret — verify each webhook request with this (see the
                  guide below).
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <TextField
                    value={secretRevealed ? signingSecret || "" : maskedSecret}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
                  />
                  <Tooltip title={secretRevealed ? "Hide" : "Reveal"}>
                    <IconButton size="small" onClick={() => setSecretRevealed((v) => !v)}>
                      {secretRevealed ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={secretCopied ? "Copied!" : "Copy"}>
                    <IconButton
                      size="small"
                      onClick={copySecret}
                      color={secretCopied ? "success" : "default"}
                    >
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
            {(saveWebhookMutation.isError || toggleWebhookMutation.isError) && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {saveWebhookMutation.error?.response?.data?.error ||
                  toggleWebhookMutation.error?.response?.data?.error ||
                  "Something went wrong. Try again."}
              </Alert>
            )}
          </>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* ── Developer guide ─────────────────────────────────── */}
      <DeveloperDocs />

      {/* ── Reroll confirmation ─────────────────────────────── */}
      <Dialog open={confirmReroll} onClose={() => setConfirmReroll(false)}>
        <DialogTitle>Reroll API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This generates a new API key and immediately revokes the current one.
            Any integration still using the old key will stop working until you
            update it. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReroll(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => rerollMutation.mutate()}
            disabled={rerollMutation.isLoading}
            sx={{ textTransform: "none" }}
          >
            {rerollMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Reroll key"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/*------------------------------------------------------------------
  MAIN PAGE
-------------------------------------------------------------------*/
export default function ProductManagement() {
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Re-check the AlphaFree gate at the page level (Navbar already hides
  // the nav entry, but the route itself should not render for anyone
  // who lands here directly).
  const { data: companyData, isLoading: isLoadingCompany } = useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    { enabled: !!userInfo?.company_id, staleTime: 1000 * 60 * 30 },
  );

  const company =
    (Array.isArray(companyData) ? companyData[0] : null) ||
    companyData?.data ||
    companyData ||
    {};

  const isAlphaFreeAccount = company.account_status === "AlphaFree";

  // Sales proceeds land in a specific GlobalUser's balance (the company's
  // logged-in user), not a company-level balance — same resolution T2A
  // itself uses (see T2AUniversalDialog / InternalTransfer). Captured here
  // so it can be stored against the product as its T2A payout owner.
  const { data: balData } = useQuery(
    ["myBalance"],
    () => client.balanceLedger.fetchMyBalance(),
    { enabled: isAlphaFreeAccount, staleTime: 1000 * 60 },
  );
  const ownerGlobalUserId = balData?.user?.id ?? null;

  const { data: products, isLoading: isLoadingProducts } = useQuery(
    ["commerce", "products"],
    () => client.commerce.listProducts(),
    { enabled: isAlphaFreeAccount, staleTime: 1000 * 60 },
  );

  // Headline metrics now live in the persistent header stat row (not
  // gated to the metrics tab), so they're fetched as soon as the page
  // loads rather than on first click into "Sales & metrics".
  const { data: metrics, isLoading: isLoadingMetrics } = useQuery(
    ["commerce", "metrics"],
    () => client.commerce.fetchMetrics(),
    { enabled: isAlphaFreeAccount, staleTime: 1000 * 60 },
  );

  const { data: purchasers, isLoading: isLoadingPurchasers } = useQuery(
    ["commerce", "purchasers"],
    () => client.commerce.fetchPurchasers(),
    { enabled: isAlphaFreeAccount && activeTab === 2, staleTime: 1000 * 30 },
  );

  const { data: returns, isLoading: isLoadingReturns } = useQuery(
    ["commerce", "returns"],
    () => client.commerce.fetchReturns(),
    { enabled: isAlphaFreeAccount && activeTab === 3, staleTime: 1000 * 30 },
  );

  // Developer portal: keys/webhooks are scoped to the merchant's commerce
  // integration, so resolve it first. If none exists (admin hasn't onboarded
  // this account yet) the tab shows an empty state.
  const { data: integrations } = useQuery(
    ["commerce", "integrations"],
    () => client.commerce.listIntegrations(),
    { enabled: isAlphaFreeAccount && activeTab === 4, staleTime: 1000 * 60 },
  );
  const integrationId = Array.isArray(integrations) ? integrations[0]?.id ?? null : null;

  const createProductMutation = useMutation(
    (payload) => {
      if (!ownerGlobalUserId) {
        return Promise.reject(
          new Error("Couldn't resolve your account balance — refresh and try again."),
        );
      }
      return client.commerce.createProduct({
        ...payload,
        owner_global_user_id: ownerGlobalUserId,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["commerce", "products"]);
        // Dialog stays open and switches to its success screen (shows the
        // generated checkout_url) — it closes itself when the user's done,
        // via onReset below.
      },
    },
  );

  // "Migrate from sales done in my online store" — bulk-imports historical
  // orders (see ImportSalesHistoryDialog.js / salesImport.utils.js). These
  // are past sales, not live payments, so they don't need owner_global_user_id
  // resolved the way live checkout does — the backend just needs to know
  // which company/product-owner they belong to (see
  // docs/cummerce/crm-commerce-mapping.md).
  const importSalesMutation = useMutation(
    (rows) => client.commerce.importSalesHistory({ orders: rows }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["commerce", "products"]);
        queryClient.invalidateQueries(["commerce", "metrics"]);
        queryClient.invalidateQueries(["commerce", "purchasers"]);
        toast.success(
          `Imported ${data?.imported_count ?? "your"} order${data?.imported_count === 1 ? "" : "s"}.`,
        );
        setImportDialogOpen(false);
      },
      onError: () => {
        toast.error("Import failed — check your CSV and try again.");
      },
    },
  );

  const tabs = useMemo(
    () => [
      { label: "Products & links" },
      { label: "Sales & metrics" },
      { label: "Purchasers" },
      { label: "Returns & issues" },
      { label: "Developer portal" },
    ],
    [],
  );

  if (!isLoadingCompany && !isAlphaFreeAccount) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", mt: 6, px: 2 }}>
        <Alert severity="info" variant="outlined">
          Blitz Commerce is currently in Alpha and only available to select
          accounts. Reach out to your account manager if you'd like early
          access.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${COMMERCE_BRAND.skyDeep} 0%, ${COMMERCE_BRAND.sky} 100%)`,
          color: "#fff",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "flex-end" }}
          spacing={2}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <StorefrontOutlinedIcon fontSize="small" sx={{ opacity: 0.9 }} />
              <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600 }}>
                Alpha
              </Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              Blitz Commerce
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 520 }}>
              Create product links, accept payments, and track sales — funds
              land directly in your Blitz Balance.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={() => setImportDialogOpen(true)}
              sx={{
                textTransform: "none",
                color: "#fff",
                borderColor: alpha("#fff", 0.5),
                "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
              }}
            >
              Import sales history
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReceiptLongOutlinedIcon />}
              onClick={() => navigate(routes.commerceTransactions)}
              sx={{
                textTransform: "none",
                color: "#fff",
                borderColor: alpha("#fff", 0.5),
                "&:hover": { borderColor: "#fff", bgcolor: alpha("#fff", 0.1) },
              }}
            >
              View transactions
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                textTransform: "none",
                bgcolor: "#fff",
                color: COMMERCE_BRAND.skyDeep,
                fontWeight: 700,
                "&:hover": { bgcolor: alpha("#fff", 0.9) },
              }}
            >
              Create product link
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        {/* ── Persistent summary row ───────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpOutlinedIcon fontSize="small" />}
              label="Total revenue"
              value={formatCurrency(metrics?.total_revenue)}
              loading={isLoadingMetrics}
              accent="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<StorefrontOutlinedIcon fontSize="small" />}
              label="Orders"
              value={metrics?.total_orders ?? 0}
              loading={isLoadingMetrics}
              accent="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<PeopleAltOutlinedIcon fontSize="small" />}
              label="Unique purchasers"
              value={metrics?.unique_purchasers ?? 0}
              loading={isLoadingMetrics}
              accent="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssignmentReturnOutlinedIcon fontSize="small" />}
              label="Refund rate"
              value={
                metrics?.refund_rate !== undefined
                  ? `${(metrics.refund_rate * 100).toFixed(1)}%`
                  : "0.0%"
              }
              loading={isLoadingMetrics}
              accent={metrics?.refund_rate > 0.02 ? "error" : "warning"}
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
            TabIndicatorProps={{ style: { backgroundColor: COMMERCE_BRAND.sky } }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.label} label={tab.label} sx={{ textTransform: "none" }} />
            ))}
          </Tabs>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {activeTab === 0 && (
              <ProductsTab
                products={products}
                isLoading={isLoadingProducts}
                onCreateClick={() => setCreateDialogOpen(true)}
              />
            )}
            {activeTab === 1 && (
              <MetricsTab metrics={metrics} isLoading={isLoadingMetrics} />
            )}
            {activeTab === 2 && (
              <PurchasersTab
                purchasers={purchasers}
                isLoading={isLoadingPurchasers}
              />
            )}
            {activeTab === 3 && (
              <ReturnsTab returns={returns} isLoading={isLoadingReturns} />
            )}
            {activeTab === 4 && (
              <DeveloperPortalTab integrationId={integrationId} />
            )}
          </Box>
        </Paper>
      </Box>

      <CreateProductLinkDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          createProductMutation.reset();
        }}
        onSubmit={(payload) => createProductMutation.mutate(payload)}
        isSubmitting={createProductMutation.isLoading}
        error={createProductMutation.error}
        createdProduct={createProductMutation.data}
        onCreateAnother={() => createProductMutation.reset()}
        companyName={company.name}
      />

      <ImportSalesHistoryDialog
        open={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
          importSalesMutation.reset();
        }}
        onImport={(rows) => importSalesMutation.mutate(rows)}
        isSubmitting={importSalesMutation.isLoading}
        error={importSalesMutation.error}
      />
    </Box>
  );
}

/*------------------------------------------------------------------
  EXPECTED API SHAPES (for backend build-out)
-------------------------------------------------------------------

client.commerce.listProducts()
  -> [{
       id, name, price, active, checkout_url, image_url, gallery_image_urls,
       units_sold, revenue, created_at
     }]

client.commerce.createProduct({
  name, price, description, image_url, gallery_image_urls, return_url, owner_global_user_id
})
  -> { id, name, price, checkout_url, image_url, gallery_image_urls, return_url, active, created_at }
  // checkout_url = `${origin}/checkout/{slug}`, the stable ProductCheckoutPage
  // link — NOT a T2A pay-request link (those are single-use). Backend
  // must persist owner_global_user_id against the product — it's who
  // checkout pays out to (see requestProductPayment below), resolved by
  // T2A's own mechanism (client.balanceLedger.fetchMyBalance().user.id),
  // NOT CompanyModel.balance — that field isn't used in this build.
  // gallery_image_urls: up to 4 secondary photo URLs, in display order —
  // image_url is always the primary/hero image shown first.
  // return_url: optional — where the buyer lands after paying. Just
  // persist + echo it back; the actual post-payment redirect is handled
  // entirely client-side (see PayRequestSuccessPage.js /
  // docs/cummerce/frontend-to-backend.md §3.1), no backend redirect logic
  // needed for this.

client.commerce.fetchPublicProduct(slug)   // GET /commerce/products/:slug — no auth
  -> {
       id, name, price, description, image_url, gallery_image_urls, active,
       company_name, company_logo_url, return_url,
     }
  // Deliberately does NOT expose owner_global_user_id to the client —
  // requestProductPayment (below) looks it up server-side instead, so an
  // anonymous buyer can never forge which GlobalUser gets paid.
  // company_name/company_logo_url: resolved server-side from the owning
  // company's profile (same one gated by AlphaFree above) — lets the
  // hosted checkout page show the merchant's real branding instead of
  // generic "Blitz Commerce". company_logo_url may be null if the
  // company has no logo on file; frontend falls back to a storefront
  // icon + "Blitz Commerce" in that case.

client.commerce.requestProductPayment(slug, { buyer_name, buyer_email })  // POST /commerce/products/:slug/checkout — no auth
  -> { token }
  // IMPORTANT: this can't be a thin passthrough to the existing
  // authenticated POST /gu-balance/request-transfer — that route (a)
  // requires the *requester's* own auth token, which no anonymous buyer
  // has, and (b) per every existing call site (T2AUniversalDialog,
  // InternalTransfer.js) never returns a token synchronously — it just
  // shows "Done," implying the pay-request link gets emailed to the
  // payer out-of-band instead. A checkout page needs the buyer to pay
  // immediately, in-page. So this needs to be a genuinely new, public,
  // service-authenticated endpoint — same pattern as the already-known
  // gap on POST /commerce/orders/complete (see backend-t2a.md) — that:
  //   1. loads the product's stored owner_global_user_id server-side
  //   2. creates a T2A request on that user's behalf (requester_id =
  //      owner_global_user_id, invoice recipient = buyer_email, label =
  //      buyer_name, amount = product.price) using trusted service
  //      credentials, not the buyer's (nonexistent) auth
  //   3. returns the resulting token synchronously so the frontend can
  //      immediately call balanceLedger.payRequestByToken(token, { rail,
  //      payer_name, payer_email }) against it — no intermediate
  //      /pay-request/:token screen, straight to Stripe/PayPal's
  //      approval_url
  // Each call mints a fresh single-use T2A request, which is what makes
  // one product link sell to unlimited buyers — the link is permanent,
  // the underlying T2A request behind it is not.
  // buyer_name/payer_name: needed so PurchasersTab's customer_name column
  // (below) has something real to show — email alone doesn't give the
  // CRM-by-proxy record a name.

client.commerce.fetchMetrics()
  -> {
       total_revenue, total_orders, unique_purchasers, refund_rate,
       by_product: [{ product_id, name, orders, revenue, returns }]
     }

client.commerce.fetchPurchasers()
  -> [{
       order_id, client_id, customer_name, customer_email,
       product_name, amount, purchased_at, status
     }]
  // client_id should reference (or trigger creation of) a CRM/client
  // record — this is the "purchasers create CRM entries by proxy"
  // behavior. status: "fulfilled" | "pending" | "refunded"
  // See commerceShared.js / CommerceTransactions.js for the additional
  // money-movement fields (payment_provider, external_transaction_id,
  // reserve_held, reserve_release_at, net_amount) that page reads
  // optimistically off this same response.

client.commerce.fetchReturns()
  -> [{
       return_id, order_id, customer_name, reason, amount,
       status, filed_at
     }]
  // status: "pending" | "approved" | "denied"

client.commerce.importSalesHistory({ orders: [{
  external_order_id, order_date, customer_name, customer_email,
  product_name, amount, status,
}] })
  -> { imported_count, skipped_count, errors: [{ row, message }] }
  // "Migrate from sales done in my online store" — bulk-imports historical
  // orders from a CSV (see ImportSalesHistoryDialog.js). Full contract,
  // including the CRM client-matching rules and why these must NOT trigger
  // live Blitz Balance crediting, is in
  // docs/cummerce/crm-commerce-mapping.md.
--------------------------------------------------------------------*/
